import { get } from 'lodash';
import React, { useCallback } from 'react';
import MultiDatePicker, { DateObject } from 'react-multi-date-picker';
import Select from 'react-select';
import DateCalendar from '../../../../components/calendars/DateCalendar/DateCalendar';
import {
  frequencyOptions,
  initiationTimeOptions,
  qualityFrequencyOptions,
  rangeFullOptions,
} from '../../../../components/init-form/settings/submition-settings-frequency/constants';
import {
  getDaysInInitiationPeriod,
  getMonthIndexByName,
  getMonthsInInitiationPeriod,
} from '../../../../components/init-form/settings/submition-settings-frequency/utils';
import { DATE_FORMAT } from '../../../../constants';

// American numeration, starting from Sunday at index 0 -- the same order the API
// reads the selected week days back in.
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// The month day pickers choose days detached from any particular month, so they are
// all anchored to a 31-day month and only the day is kept.
const ANY_MONTH = new DateObject({ year: 2024, month: 12, day: 1 });

const ClearButton = ({ onClick }) => (
  <button onClick={onClick} className={'clear'}>
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clipPath="url(#clip0_5205_22763)">
        <path
          d="M12.5001 7.5013L7.50008 12.5013M7.50008 7.5013L12.5001 12.5013M18.3334 10.0013C18.3334 14.6037 14.6025 18.3346 10.0001 18.3346C5.39771 18.3346 1.66675 14.6037 1.66675 10.0013C1.66675 5.39893 5.39771 1.66797 10.0001 1.66797C14.6025 1.66797 18.3334 5.39893 18.3334 10.0013Z"
          stroke="#D92D20"
          strokeWidth="1.67"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_5205_22763">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </button>
);

// A frequency that is missing altogether is reported on the setting itself, anything
// else per field -- and either may arrive as a bare string or wrapped in a list.
const firstMessage = (error) => (Array.isArray(error) ? error[0] : error);

const TaskFrequencySettings = ({ formData, handleFormChange, errors }) => {
  const settingsError = typeof errors === 'string' || Array.isArray(errors) ? firstMessage(errors) : null;
  const getFieldError = (key) => (settingsError ? null : firstMessage(errors?.[key]));
  const frequency = formData.frequency || {};
  const frequencyType = frequency.frequency_type?.value;
  const periodType = frequency.type?.value;
  const firstInitiationDay = frequency.first_initiation_day ?? null;
  const lastInitiationDay = frequency.last_initiation_day ?? null;
  const daysInInitiationPeriod = getDaysInInitiationPeriod(firstInitiationDay, lastInitiationDay);
  const today = new DateObject(new Date());

  const handleFrequencyChange = useCallback((key, value) => handleFormChange(`frequency.${key}`, value), [handleFormChange]);

  // Switching what the frequency is built from invalidates everything specified
  // underneath it, so the settings start over from the new choice.
  const handleFrequencyTypeChange = useCallback((option) => handleFormChange('frequency', { frequency_type: option }), [handleFormChange]);

  const handlePeriodTypeChange = useCallback(
    (option) => {
      handleFrequencyChange('type', option);
      handleFrequencyChange('week_days', []);
      handleFrequencyChange('months', []);
      handleFrequencyChange('month_day', []);
    },
    [handleFrequencyChange],
  );

  // The days and months on offer depend on the initiation period, so a new period
  // drops what was picked under the previous one.
  const handleInitiationDayChange = useCallback(
    (date, key) => {
      handleFrequencyChange(key, date);
      handleFrequencyChange('week_days', []);
      handleFrequencyChange('months', []);
      handleFrequencyChange('month_day', []);
    },
    [handleFrequencyChange],
  );

  const renderInitiationTime = () => (
    <div className={'form-item form-item-select mb-4'}>
      <label className="form-label">Initiation Time</label>
      <Select
        menuPlacement="top"
        options={initiationTimeOptions}
        className="form-control-select"
        classNamePrefix="react-select"
        isSearchable={false}
        value={frequency.time ?? null}
        placeholder="Choose Time"
        onChange={(option) => handleFrequencyChange('time', option)}
      />
      {getFieldError('time') && <span className="form-error mt-1">{getFieldError('time')}</span>}
      <span className={'text-secondary'}>
        The forms of the task will be initiated for submission on the selected dates, at the selected time.
      </span>
    </div>
  );

  const renderInitiationMonthDays = () => (
    <div className={'form-item multi-date-picker disabled-change-month mb-4'}>
      <label className="form-label">Initiation Date</label>
      <div className={'clear-datepicker'}>
        <MultiDatePicker
          multiple
          format={'DD'}
          editable={false}
          placeholder={'Choose Date'}
          currentDate={ANY_MONTH}
          mapDays={({ date }) => ({
            disabled: lastInitiationDay ? !daysInInitiationPeriod.includes(date.day) : false,
          })}
          value={frequency.month_day ?? []}
          onChange={(dates) => handleFrequencyChange('month_day', dates)}
        />
        {frequency.month_day?.length > 0 && <ClearButton onClick={() => handleFrequencyChange('month_day', [])} />}
      </div>
      {getFieldError('month_day') && <span className="form-error mb-2">{getFieldError('month_day')}</span>}
    </div>
  );

  const renderCustomDatesSettings = () => (
    <>
      <div className={'form-item multi-date-picker mb-4'}>
        <label className="form-label">Initiation Date</label>
        <div className={'clear-datepicker'}>
          <MultiDatePicker
            multiple
            editable={false}
            minDate={today}
            placeholder={'Choose Date'}
            format={DATE_FORMAT}
            value={frequency.specific_dates ?? []}
            onChange={(dates) => handleFrequencyChange('specific_dates', dates)}
          />
          {frequency.specific_dates?.length > 0 && <ClearButton onClick={() => handleFrequencyChange('specific_dates', [])} />}
        </div>
        {getFieldError('specific_dates') && <span className="form-error mb-2">{getFieldError('specific_dates')}</span>}
      </div>
      {renderInitiationTime()}
    </>
  );

  const renderCustomIntervalSettings = () => (
    <>
      <div className="d-flex time-select mb-0" style={{ alignItems: 'flex-start' }}>
        <div className={'form-item form-item-select mb-4'} style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <label className="form-label">First initiation day</label>
          <DateCalendar
            isClearable
            minDate={new Date()}
            maxDate={lastInitiationDay}
            selectedDate={firstInitiationDay}
            placeholderText="Choose Date"
            handleChange={(date) => handleInitiationDayChange(date, 'first_initiation_day')}
          />
          {getFieldError('first_initiation_day') && <span className="form-error mt-1">{getFieldError('first_initiation_day')}</span>}
        </div>
        <div className={'form-item form-item-select mb-4'} style={{ display: 'inline-flex', flexDirection: 'column' }}>
          <label className="form-label">Last initiation day (optional)</label>
          <DateCalendar
            isClearable
            minDate={firstInitiationDay ?? new Date()}
            selectedDate={lastInitiationDay}
            placeholderText="Choose Date"
            handleChange={(date) => handleInitiationDayChange(date, 'last_initiation_day')}
          />
          {getFieldError('last_initiation_day') && <span className="form-error mt-1">{getFieldError('last_initiation_day')}</span>}
        </div>
      </div>

      <div className={'d-flex mb-0 time-select'} style={{ alignItems: 'flex-start' }}>
        <div className={'form-item form-item-select mb-4'}>
          <label className="form-label">Choose period</label>
          <Select
            options={qualityFrequencyOptions}
            className="form-control-select"
            classNamePrefix="react-select"
            value={frequency.times ?? null}
            placeholder="Choose quality"
            onChange={(option) => handleFrequencyChange('times', option)}
          />
          {getFieldError('times') && <span className="form-error mt-1">{getFieldError('times')}</span>}
        </div>
        <div className={'form-item form-item-select mb-4'}>
          <label className="form-label opacity-0">Type</label>
          <Select
            options={rangeFullOptions}
            className="form-control-select"
            classNamePrefix="react-select"
            value={frequency.type ?? null}
            placeholder="Choose period"
            onChange={handlePeriodTypeChange}
          />
          {getFieldError('type') && <span className="form-error mt-1">{getFieldError('type')}</span>}
        </div>
      </div>

      {periodType === 'weekly' && (
        <div className={'form-item mb-4'}>
          <label>Repeat on these days:</label>
          <div className={'week-grid'}>
            {WEEK_DAYS.map((weekDay, index) => (
              <div key={weekDay} className={'form-control-checkbox-span'}>
                <input
                  type="checkbox"
                  checked={!!get(frequency, `week_days[${index}]`)}
                  onChange={(e) => handleFrequencyChange(`week_days[${index}]`, e.target.checked)}
                />
                <span>{weekDay}</span>
              </div>
            ))}
          </div>
          {getFieldError('week_days') && <span className="form-error mb-2">{getFieldError('week_days')}</span>}
        </div>
      )}

      {periodType === 'monthly' && firstInitiationDay && renderInitiationMonthDays()}

      {periodType === 'yearly' && (
        <>
          <div className={'form-item mb-4'}>
            <label>Repeat on these months:</label>
            <div className={'week-grid month-grid'}>
              {getMonthsInInitiationPeriod(firstInitiationDay, lastInitiationDay).map((month) => {
                const monthIndex = getMonthIndexByName(month);

                return (
                  <div key={month} className={'form-control-checkbox-span'}>
                    <input
                      type="checkbox"
                      checked={!!get(frequency, `months[${monthIndex}]`)}
                      onChange={(e) => handleFrequencyChange(`months[${monthIndex}]`, e.target.checked)}
                    />
                    <span>{month}</span>
                  </div>
                );
              })}
            </div>
            {getFieldError('months') && <span className="form-error mb-2">{getFieldError('months')}</span>}
          </div>
          {renderInitiationMonthDays()}
        </>
      )}

      {periodType && renderInitiationTime()}
    </>
  );

  return (
    <div className="form-control-item-settings">
      <div className="form-settings-label">
        <label className="form-label form-label-big">Does the task have a frequency?</label>
        <span>
          Define whether there is a recurring pattern or regularity associated with how often the forms of this task need to be filled out
          or submitted.
        </span>
      </div>
      <div className="form-settings-inputs">
        <div className="d-flex">
          <div className="form-control-radio">
            <input
              name="will_have_frequency"
              type="radio"
              checked={formData.will_have_frequency === true}
              onChange={() => handleFormChange('will_have_frequency', true)}
            />
            <span>Yes</span>
          </div>
          <div className="form-control-radio">
            <input
              name="will_have_frequency"
              type="radio"
              checked={formData.will_have_frequency === false}
              onChange={() => handleFormChange('will_have_frequency', false)}
            />
            <span>No</span>
          </div>
        </div>
        {settingsError && <span className="form-error mb-2">{settingsError}</span>}

        {formData.will_have_frequency && (
          <>
            <div className={'form-item form-item-select mb-4'}>
              <label className="form-label">Frequency</label>
              <Select
                options={frequencyOptions}
                className="form-control-select"
                classNamePrefix="react-select"
                isSearchable={false}
                value={frequency.frequency_type ?? null}
                placeholder="Choose"
                onChange={handleFrequencyTypeChange}
              />
              {getFieldError('frequency_type') && <span className="form-error mb-2">{getFieldError('frequency_type')}</span>}
            </div>

            {frequencyType === 'specific_dates' && renderCustomDatesSettings()}
            {frequencyType === 'custom_interval' && renderCustomIntervalSettings()}
          </>
        )}
      </div>
    </div>
  );
};

export default TaskFrequencySettings;
